import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Copy,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/cn.ts'
import { Button, IconButton } from './button.tsx'

export interface PageHeaderProps {
  title: string
  description?: string
  action?: ReactNode
}

export function PageHeader({ title, description, action }: PageHeaderProps) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-headline-md font-medium text-on-surface">{title}</h1>
        {description ? (
          <p className="mt-1 text-body-md text-on-surface-variant">{description}</p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </header>
  )
}

export interface PaginationProps {
  page: number
  totalPages: number
  total: number
  onPageChange: (page: number) => void
  pageSize?: number
  pageSizes?: readonly number[]
  onPageSizeChange?: (size: number) => void
}

export function Pagination({
  page,
  totalPages,
  total,
  onPageChange,
  pageSize,
  pageSizes,
  onPageSizeChange,
}: PaginationProps) {
  if (total === 0) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 pt-4">
      <p className="text-label-sm text-on-surface-variant">
        Halaman {page} dari {Math.max(totalPages, 1)} · {total} data
      </p>

      <div className="flex items-center gap-2">
        {pageSizes && pageSize && onPageSizeChange ? (
          <select
            aria-label="Jumlah baris per halaman"
            value={pageSize}
            onChange={(event) => onPageSizeChange(Number(event.target.value))}
            className="h-10 rounded-full bg-surface-muted px-4 text-label-md text-on-surface outline-none focus-visible:outline-2 focus-visible:outline-primary"
          >
            {pageSizes.map((size) => (
              <option key={size} value={size}>
                {size} / halaman
              </option>
            ))}
          </select>
        ) : null}

        <IconButton
          label="Halaman sebelumnya"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeft className="size-5" />
        </IconButton>
        <IconButton
          label="Halaman berikutnya"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRight className="size-5" />
        </IconButton>
      </div>
    </div>
  )
}

export interface CopyButtonProps {
  value: string
  label?: string
  variant?: 'icon' | 'button'
}

/** Copies to the clipboard and confirms inline, so no toast is needed. */
export function CopyButton({ value, label = 'Salin', variant = 'icon' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be blocked; the value stays selectable on screen.
    }
  }

  if (variant === 'button') {
    return (
      <Button
        variant="tonal"
        onClick={() => void copy()}
        icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
      >
        {copied ? 'Tersalin' : label}
      </Button>
    )
  }

  return (
    <IconButton label={copied ? 'Tersalin' : label} onClick={() => void copy()}>
      {copied ? <Check className="size-4 text-success" /> : <Copy className="size-4" />}
    </IconButton>
  )
}

/** Horizontally scrollable wrapper so wide tables stay usable on a phone. */
export function TableScroller({ children }: { children: ReactNode }) {
  return <div className="-mx-6 overflow-x-auto px-6">{children}</div>
}

export function Th({ className, children }: { className?: string; children?: ReactNode }) {
  return (
    <th
      scope="col"
      className={cn(
        'whitespace-nowrap px-4 py-3 text-left text-label-sm font-medium text-on-surface-variant',
        className,
      )}
    >
      {children}
    </th>
  )
}

export function Td({ className, children }: { className?: string; children: ReactNode }) {
  return <td className={cn('px-4 py-3 text-body-md text-on-surface', className)}>{children}</td>
}

export interface SortableThProps {
  label: string
  /** Null when the table is currently sorted by some other column. */
  direction: 'asc' | 'desc' | null
  onToggle: () => void
  className?: string
}

/** A column header that sorts. The sort itself happens on the server. */
export function SortableTh({ label, direction, onToggle, className }: SortableThProps) {
  const Icon = direction === 'asc' ? ArrowUp : direction === 'desc' ? ArrowDown : ChevronsUpDown

  return (
    <th
      scope="col"
      aria-sort={
        direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'
      }
      className={cn('whitespace-nowrap px-4 py-3 text-left', className)}
    >
      <button
        type="button"
        onClick={onToggle}
        className={cn(
          'inline-flex items-center gap-1 rounded-full px-2 py-1 -mx-2',
          'text-label-sm font-medium transition-colors',
          'hover:bg-primary/10 focus-visible:outline-2 focus-visible:outline-primary',
          direction ? 'text-on-surface' : 'text-on-surface-variant',
        )}
      >
        {label}
        <Icon className="size-3.5" aria-hidden="true" />
      </button>
    </th>
  )
}
